/**
 * Plan Service
 * Handles CRUD operations for waste management plans
 */

import { apiClient } from './api';
import type { Plan, PlanData, PlanStatus, PlanBidWithPricing, ServiceProvider, AcceptBidResponse } from '../types';
import { safeJsonParse, isPlainObject } from '@/lib/utils';
import {
  ensurePlanArray,
  ensurePlanResponse,
  ensureCreatePlanResponse,
  isPlan,
  ensureGetPlanBidsResponse,
  ensurePlanAcceptBidResponse,
  ensurePlanBidActionResponse,
} from '@/lib/api/type-guards';

interface CreatePlanResponse {
  plan: Plan;
  plan_url: string;
}

/**
 * Create a safe empty PlanData object for use as a fallback
 */
function createEmptyPlanData(): PlanData {
  return {
    project_info: {
      project_name: '',
      project_address: '',
      project_type: 'new_construction',
      estimated_start_date: '',
      estimated_completion_date: '',
    },
    applicant_info: {
      contact_name: '',
      contact_phone: '',
      contact_email: '',
    },
    waste_streams: [],
    compliance: {
      total_waste_generated: 0,
      total_waste_diverted: 0,
      diversion_rate: 0,
      meets_calgreen_requirement: false,
    },
    version: '1.0',
  };
}

interface GetBidsResponse {
  bids: (PlanBidWithPricing & { service_provider: ServiceProvider })[];
  total: number;
}

interface BidActionResponse {
  bid: PlanBidWithPricing;
}

export const planService = {
  /**
   * Get all plans for the current user (deprecated - kept for compatibility)
   */
  async getAllPlans(): Promise<Plan[]> {
    const response = await apiClient.get<Plan[]>('/plans');
    return ensurePlanArray(response);
  },

  /**
   * Get a single plan by ID (no authentication required)
   */
  async getPlan(planId: string): Promise<Plan> {
    const response = await apiClient.get<{ plan: Plan }>(`/plans/${planId}`);
    return ensurePlanResponse(response).plan;
  },

  /**
   * Create a new plan
   * Returns the plan and a shareable URL
   */
  async createPlan(planData: PlanData, applicantEmail?: string): Promise<CreatePlanResponse> {
    const response = await apiClient.post<CreatePlanResponse>('/plans', {
      plan_data: JSON.stringify(planData),
      status: 'draft',
      ...(applicantEmail && { applicant_email: applicantEmail }),
    });
    return ensureCreatePlanResponse(response);
  },

  /**
   * Update an existing plan
   */
  async updatePlan(planId: string, planData: Partial<PlanData>): Promise<Plan> {
    // Get the current plan first to merge data
    const currentPlan = await this.getPlan(planId);
    const currentData = safeJsonParse<PlanData>(
      currentPlan.plan_data,
      (value): value is PlanData => isPlainObject(value),
      createEmptyPlanData()
    );

    const updatedData: PlanData = {
      ...currentData,
      ...planData,
    };

    const response = await apiClient.put<{ plan: Plan }>(`/plans/${planId}`, {
      plan_data: JSON.stringify(updatedData),
    });
    return ensurePlanResponse(response).plan;
  },

  /**
   * Update plan status
   */
  async updatePlanStatus(planId: string, status: PlanStatus): Promise<Plan> {
    const response = await apiClient.patch<Plan>(`/plans/${planId}/status`, { status });
    // Validate that the response is a Plan object
    if (!isPlan(response)) {
      console.warn('Invalid Plan response from updatePlanStatus:', response);
      throw new Error('Invalid plan response');
    }
    return response;
  },

  /**
   * Delete a plan
   */
  async deletePlan(planId: string): Promise<void> {
    return apiClient.delete<void>(`/plans/${planId}`);
  },

  /**
   * Parse plan_data JSON string into PlanData object
   */
  parsePlanData(plan: Plan): PlanData {
    return safeJsonParse<PlanData>(
      plan.plan_data,
      (value): value is PlanData => isPlainObject(value),
      createEmptyPlanData()
    );
  },

  /**
   * Get all bids for a plan
   */
  async getBids(planId: string): Promise<GetBidsResponse> {
    const response = await apiClient.get<GetBidsResponse>(`/plans/${planId}/bids`);
    const validated = ensureGetPlanBidsResponse(response);
    // Cast to GetBidsResponse since the API returns bids with service_provider included
    return validated as GetBidsResponse;
  },

  /**
   * Accept a bid (and auto-reject others)
   */
  async acceptBid(planId: string, bidId: string): Promise<AcceptBidResponse> {
    const response = await apiClient.post<AcceptBidResponse>(
      `/plans/${planId}/bids/${bidId}/accept`
    );
    return ensurePlanAcceptBidResponse(response);
  },

  /**
   * Reject a bid
   */
  async rejectBid(planId: string, bidId: string): Promise<PlanBidWithPricing> {
    const response = await apiClient.post<BidActionResponse>(
      `/plans/${planId}/bids/${bidId}/reject`
    );
    return ensurePlanBidActionResponse(response).bid;
  },

  /**
   * Resend plan confirmation email
   */
  async resendConfirmationEmail(planId: string): Promise<void> {
    await apiClient.post(`/plans/${planId}/send-confirmation`);
  },

  /**
   * Publish plan to marketplace
   * Changes plan status from 'draft' to 'approved' after validation
   */
  async publishPlan(planId: string): Promise<Plan> {
    const response = await apiClient.post<{ plan: Plan }>(`/plans/${planId}/publish`);
    return ensurePlanResponse(response).plan;
  },
};